import coffeeFiltersV4 from '../config/coffeeFiltersV4.json';

// Type definitions
export interface Type {
  id: string;
  label: string;
  category_id: string;
  priority: number;
  keywords: string[];
  exclude: string[];
}

export interface Category {
  id: string;
  label: string;
  type_ids: string[];
  ui_rules?: { hide_niche_when_zero?: boolean };
}

interface Config {
  version: string;
  ui_levels: {
    level1: { id: string; options: Array<{ id: string; label: string }>; default: string };
    level2: { id: string; shown_when_mode: string; ordered_category_ids: string[] };
    level3: { id: string; shown_when_mode: string };
  };
  popular: { ordered_type_ids: string[] };
  categories: Category[];
  types: Type[];
  early_filtering: {
    precompute_fields_on_ingest: string[];
    exclude_bundles_and_retail_first: boolean;
    bundle_keywords: string[];
    retail_keywords: string[];
  };
}

const config = coffeeFiltersV4 as Config;

// Preprocess item name for matching
function preprocessItemName(name: string): string {
  if (!name || typeof name !== 'string') return '';
  return name
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' '); // Collapse whitespace
}

// Check if item should be excluded (bundles/retail)
function shouldExcludeItem(name: string): boolean {
  const processed = preprocessItemName(name);
  const { bundle_keywords, retail_keywords } = config.early_filtering;
  
  // Check bundle keywords
  for (const keyword of bundle_keywords) {
    if (processed.includes(keyword.toLowerCase())) {
      return true;
    }
  }
  
  // Check retail keywords
  for (const keyword of retail_keywords) {
    if (processed.includes(keyword.toLowerCase())) {
      return true;
    }
  }
  
  return false;
}

// Match item name to a type (first match wins based on priority)
export function matchItemToType(itemName: string): string | null {
  if (!itemName || typeof itemName !== 'string') return null;
  
  // Early exclusion check
  if (config.early_filtering.exclude_bundles_and_retail_first && shouldExcludeItem(itemName)) {
    return null;
  }
  
  const processed = preprocessItemName(itemName);
  
  // Sort types by priority (lower priority = checked first)
  const sortedTypes = [...config.types].sort((a, b) => a.priority - b.priority);
  
  // First match wins
  for (const type of sortedTypes) {
    // Check if any keyword matches
    const hasKeyword = type.keywords.some(keyword => 
      processed.includes(keyword.toLowerCase())
    );
    
    if (!hasKeyword) continue;
    
    // Check if any exclude keyword matches
    const hasExclude = type.exclude.length > 0 && type.exclude.some(exclude => 
      processed.includes(exclude.toLowerCase())
    );
    
    if (hasExclude) continue;
    
    // First match found - return immediately
    return type.id;
  }
  
  return null;
}

// Get all type IDs for a location
export function getLocationTypeIds(location: any): Set<string> {
  const typeIds = new Set<string>();
  
  // Check coffee_menu format
  if (location.coffee_menu && typeof location.coffee_menu === 'object') {
    Object.keys(location.coffee_menu).forEach(itemName => {
      const typeId = matchItemToType(itemName);
      if (typeId) {
        typeIds.add(typeId);
      }
    });
  }
  
  // Check legacy format (coffeeHotDrinks)
  if (location.coffeeHotDrinks && Array.isArray(location.coffeeHotDrinks)) {
    location.coffeeHotDrinks.forEach((drink: any) => {
      if (drink.name) {
        const typeId = matchItemToType(drink.name);
        if (typeId) {
          typeIds.add(typeId);
        }
      }
    });
  }
  
  // Check legacy format (icedCoffeeTea)
  if (location.icedCoffeeTea && Array.isArray(location.icedCoffeeTea)) {
    location.icedCoffeeTea.forEach((drink: any) => {
      if (drink.name) {
        const typeId = matchItemToType(drink.name);
        if (typeId) {
          typeIds.add(typeId);
        }
      }
    });
  }
  
  return typeIds;
}

// Get all category IDs for a location (from its type IDs)
export function getLocationCategoryIds(location: any): Set<string> {
  const typeIds = getLocationTypeIds(location);
  const categoryIds = new Set<string>();
  
  // Map type IDs to category IDs
  typeIds.forEach(typeId => {
    const type = config.types.find(t => t.id === typeId);
    if (type) {
      categoryIds.add(type.category_id);
    }
  });
  
  return categoryIds;
}

// Preprocess location to add type_ids and category_ids
export function preprocessLocation(location: any): any {
  if (!location) return location;
  
  const typeIds = getLocationTypeIds(location);
  const categoryIds = getLocationCategoryIds(location);
  
  // Add precomputed fields
  return {
    ...location,
    _typeIds: Array.from(typeIds),
    _categoryIds: Array.from(categoryIds),
    // Also preprocess coffee_menu items
    coffee_menu: location.coffee_menu && typeof location.coffee_menu === 'object'
      ? Object.entries(location.coffee_menu).reduce((acc: any, [itemName, menuItem]: [string, any]) => {
          const typeId = matchItemToType(itemName);
          if (typeId) {
            acc[itemName] = {
              ...(typeof menuItem === 'object' ? menuItem : {}),
              _typeId: typeId
            };
          } else {
            acc[itemName] = menuItem;
          }
          return acc;
        }, {})
      : location.coffee_menu,
    // Preprocess legacy format
    coffeeHotDrinks: location.coffeeHotDrinks && Array.isArray(location.coffeeHotDrinks)
      ? location.coffeeHotDrinks.map((drink: any) => {
          if (drink.name) {
            const typeId = matchItemToType(drink.name);
            return { ...drink, typeId };
          }
          return drink;
        })
      : location.coffeeHotDrinks,
    icedCoffeeTea: location.icedCoffeeTea && Array.isArray(location.icedCoffeeTea)
      ? location.icedCoffeeTea.map((drink: any) => {
          if (drink.name) {
            const typeId = matchItemToType(drink.name);
            return { ...drink, typeId };
          }
          return drink;
        })
      : location.icedCoffeeTea
  };
}

// Check if location has a specific type
export function locationHasType(location: any, typeId: string): boolean {
  // Use precomputed typeIds if available
  if (location._typeIds && Array.isArray(location._typeIds)) {
    return location._typeIds.includes(typeId);
  }
  
  // Fallback to runtime check
  const typeIds = getLocationTypeIds(location);
  return typeIds.has(typeId);
}

// Check if location has a specific category
export function locationHasCategory(location: any, categoryId: string): boolean {
  // Use precomputed categoryIds if available
  if (location._categoryIds && Array.isArray(location._categoryIds)) {
    return location._categoryIds.includes(categoryId);
  }
  
  // Fallback to runtime check
  const categoryIds = getLocationCategoryIds(location);
  return categoryIds.has(categoryId);
}

// Get type label
export function getTypeLabel(typeId: string): string {
  const type = config.types.find(t => t.id === typeId);
  return type ? type.label : typeId;
}

// Get category label
export function getCategoryLabel(categoryId: string): string {
  const category = config.categories.find(c => c.id === categoryId);
  return category ? category.label : categoryId;
}

// Get popular type IDs
export function getPopularTypeIds(): string[] {
  return config.popular.ordered_type_ids;
}

// Get categories
export function getCategories(): Category[] {
  return config.categories.filter(cat => 
    config.ui_levels.level2.ordered_category_ids.includes(cat.id)
  ).sort((a, b) => {
    const indexA = config.ui_levels.level2.ordered_category_ids.indexOf(a.id);
    const indexB = config.ui_levels.level2.ordered_category_ids.indexOf(b.id);
    return indexA - indexB;
  });
}

// Get types for a category
export function getTypesForCategory(categoryId: string): Type[] {
  const category = config.categories.find(c => c.id === categoryId);
  if (!category) return [];
  
  return category.type_ids
    .map(typeId => config.types.find(t => t.id === typeId))
    .filter((t): t is Type => t !== undefined)
    .sort((a, b) => a.priority - b.priority);
}

// Get all types (for popular mode)
export function getAllTypes(): Type[] {
  return [...config.types].sort((a, b) => a.priority - b.priority);
}
